import { Controller } from '@hotwired/stimulus';

// The per-goal "Show STGs" toggles: the page header's status filter, scoped to
// one long-term goal. Independent on and off, several combining as a union, no
// All. The server renders every child of the goal into the DOM and seeds this
// controller from the page's selection, so a card opens agreeing with the
// header; from then on toggling here changes this card and nothing else, until
// the next page load re-seeds it.
//
// Because the whole family is in the DOM, the counts on the buttons are the
// goal's real ones. That is what makes turning a status on here able to reveal
// a child the page filter excluded.
//
// Targets:
//   stg    - one per short-term goal card, carrying data-status
//   toggle - one per status button, carrying data-status and data-label (the
//            status as the button names it, which the tooltip repeats)
//   badge  - the "<shown>/<total> STGs" indicator in the goal's own row
//   empty  - the line saying every status is off; a selection that matches no
//            child needs none, since the badge's count already says so
export default class extends Controller {
  static targets = ['stg', 'toggle', 'badge', 'empty'];

  toggle(event) {
    const button = event.currentTarget;
    const on = button.getAttribute('aria-pressed') !== 'true';
    // The tooltip says what the button says: voided goals read "deleted", so
    // the status key is only a fallback for a button rendered without a label.
    const label = button.dataset.label || button.dataset.status || '';
    button.setAttribute('aria-pressed', String(on));
    button.classList.toggle('active', on);
    button.title = on ? `Hide ${label} short term goals` : `Show ${label} short term goals`;
    this.apply();
  }

  apply() {
    const selected = this.selected();
    let shown = 0;
    this.stgTargets.forEach((stg) => {
      const visible = selected.includes(stg.dataset.status || '');
      stg.classList.toggle('d-none', !visible);
      if (visible) shown += 1;
    });

    if (this.hasBadgeTarget) {
      const total = this.badgeTarget.dataset.total || String(this.stgTargets.length);
      this.badgeTarget.textContent = `${shown}/${total} STGs`;
    }

    if (this.hasEmptyTarget) {
      this.emptyTarget.hidden = selected.length > 0 || this.stgTargets.length === 0;
    }
  }

  // The buttons are the state, so there is nothing to keep in sync with them.
  selected() {
    return this.toggleTargets
      .filter((button) => button.getAttribute('aria-pressed') === 'true')
      .map((button) => button.dataset.status || '');
  }
}
